import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, Image, X, AlertCircle, Loader2 } from 'lucide-react';
import { BulkUploadDocumentView } from '../../../../api/General/bulkUpload';

const MAX_FILES = 100;
const ALLOWED_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);
const ALLOWED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png';

interface FileUploadStepProps {
  uploadedDocuments: BulkUploadDocumentView[];
  uploading: boolean;
  onFilesSelected: (files: File[]) => void;
  onRemoveDocument: (documentId: number) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return <Image className="w-5 h-5 text-blue-500" />;
  return <FileText className="w-5 h-5 text-red-500" />;
}

const FileUploadStep: React.FC<FileUploadStepProps> = ({
  uploadedDocuments,
  uploading,
  onFilesSelected,
  onRemoveDocument,
}) => {
  const { t } = useTranslation('common');
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndSelect = useCallback(
    (fileList: FileList | File[]) => {
      setValidationError(null);
      const files = Array.from(fileList);

      // Check batch limit
      const totalAfter = uploadedDocuments.length + files.length;
      if (totalAfter > MAX_FILES) {
        setValidationError(
          t('Batch limit is {{max}} files. You have {{current}} and are trying to add {{adding}}.', {
            max: MAX_FILES,
            current: uploadedDocuments.length,
            adding: files.length,
          })
        );
        return;
      }

      // Check MIME types
      const invalid = files.filter((f) => !ALLOWED_TYPES.has(f.type));
      if (invalid.length > 0) {
        setValidationError(
          t('Invalid file types: {{names}}. Only PDF, JPG, and PNG are accepted.', {
            names: invalid.map((f) => f.name).join(', '),
          })
        );
        return;
      }

      onFilesSelected(files);
    },
    [uploadedDocuments.length, onFilesSelected, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files?.length) {
        validateAndSelect(e.dataTransfer.files);
      }
    },
    [validateAndSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        validateAndSelect(e.target.files);
        e.target.value = '';
      }
    },
    [validateAndSelect]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`
          relative flex flex-col items-center justify-center gap-4 p-10 rounded-xl border-2 border-dashed transition-all cursor-pointer
          ${dragActive ? 'border-primary bg-primary/5 scale-[1.01]' : 'border-border hover:border-primary/50 hover:bg-muted/30'}
          ${uploading ? 'opacity-60 pointer-events-none' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS}
          onChange={handleFileInput}
          className="hidden"
        />
        {uploading ? (
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
        ) : (
          <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary">
            <Upload className="w-7 h-7" />
          </div>
        )}
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">
            {uploading ? t('Uploading files...') : t('Drag and drop files here, or click to browse')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('Supported: PDF, JPG, PNG')} &middot; {t('Max {{max}} files per batch', { max: MAX_FILES })}
          </p>
        </div>
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{validationError}</p>
        </div>
      )}

      {/* Uploaded files list */}
      {uploadedDocuments.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">
              {t('Uploaded Files')} ({uploadedDocuments.length})
            </p>
          </div>
          <div className="border border-border rounded-lg divide-y divide-border max-h-80 overflow-y-auto">
            {uploadedDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
              >
                {getFileIcon(doc.contentType)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.originalFileName}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(doc.fileSize)}</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveDocument(doc.id);
                  }}
                  className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={t('Remove file')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadStep;
